import React, { useEffect } from 'react';
import { X } from 'lucide-react';

export const cx = (...a) => a.filter(Boolean).join(' ');
export const CARD = 'rounded-[20px] border border-[var(--line)] bg-[var(--surface)] shadow-[var(--shadow-sm)]';
export const INPUT = 'w-full rounded-xl border border-[var(--line)] bg-[var(--surface-2)] px-3.5 py-2.5 text-sm text-[var(--ink)] outline-none transition-colors placeholder:text-[var(--faint)] focus:border-[var(--accent)]';
export const LABEL = 'mb-1.5 block font-[var(--mono)] text-[10px] font-bold uppercase tracking-[.12em] text-[var(--faint)]';
const BTN = 'inline-flex items-center justify-center gap-1.5 rounded-full font-semibold transition-[transform,opacity,background-color] duration-150 active:scale-[.97] disabled:cursor-not-allowed disabled:opacity-40';
const SIZE = { sm: 'px-3 py-1.5 text-xs', md: 'px-4 py-2 text-sm', lg: 'px-5 py-3 text-base' };
const TONE = {
  solid: 'bg-[var(--solid)] text-[var(--on-solid)] hover:opacity-90',
  accent: 'bg-[var(--accent)] text-white hover:opacity-90',
  green: 'bg-[var(--green)] text-white hover:opacity-90',
  ghost: 'border border-[var(--line)] bg-[var(--surface)] text-[var(--ink-2)] hover:bg-[var(--hover)]',
  quiet: 'text-[var(--muted)] hover:bg-[var(--hover)] hover:text-[var(--ink)]',
  danger: 'text-[var(--red)] hover:bg-[var(--hover)]',
};
export const Btn = ({ tone = 'ghost', size = 'md', className, ...p }) => <button type="button" className={cx(BTN, SIZE[size], TONE[tone], className)} {...p} />;

export const TYPE_STYLE = {
  lead: 'bg-[var(--accent-soft)] text-[var(--accent-ink)]',
  active_client: 'bg-[var(--green-bg)] text-[var(--green-ink)]',
  approved: 'bg-[var(--violet-bg)] text-[var(--violet-ink)]',
  settled: 'bg-[var(--amber-bg)] text-[var(--amber-ink)]',
  past_client: 'bg-[var(--hover)] text-[var(--muted)]',
  contact: 'bg-[var(--hover)] text-[var(--ink-2)]',
};
export const Pill = ({ className, children, ...p }) => <span className={cx('inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold', className)} {...p}>{children}</span>;
export const TypeBadge = ({ type, label, ...p }) => <Pill className={TYPE_STYLE[type] || TYPE_STYLE.contact} {...p}>{label}</Pill>;

const TINTS = ['bg-[var(--accent-soft)] text-[var(--accent-ink)]', 'bg-[var(--green-bg)] text-[var(--green-ink)]', 'bg-[var(--violet-bg)] text-[var(--violet-ink)]', 'bg-[var(--amber-bg)] text-[var(--amber-ink)]'];
export const initials = (n) => (n || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
export const Avatar = ({ name, size = 'h-9 w-9 text-xs' }) => {
  const t = TINTS[[...(name || '')].reduce((a, c) => a + c.charCodeAt(0), 0) % TINTS.length];
  return <span className={cx('grid shrink-0 place-items-center rounded-full font-bold', size, t)}>{initials(name)}</span>;
};

export const ago = (iso) => {
  if (!iso) return '—';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' });
};
export const when = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '');

// Rendered inside the page (not a portal) so light/dark tokens apply.
export const Modal = ({ open, onClose, title, children, footer, wide, testid }) => {
  useEffect(() => { if (!open) return undefined; const f = (e) => e.key === 'Escape' && onClose(); window.addEventListener('keydown', f); return () => window.removeEventListener('keydown', f); }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 p-0 backdrop-blur-sm sm:items-center sm:p-4" onClick={onClose} data-testid={testid}>
      <div className={cx(CARD, 'w-full rounded-b-none p-5 sm:rounded-[20px]', wide ? 'sm:max-w-2xl' : 'sm:max-w-md')} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h3 className="dw-ink text-base font-bold tracking-tight">{title}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-full p-1.5 text-[var(--muted)] hover:bg-[var(--hover)]" data-testid={testid && `${testid}-close`}><X className="h-4 w-4" /></button>
        </div>
        {children}
        {footer && <div className="mt-5 flex flex-wrap justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
};

export const Stat = ({ label, value, hint, tone, testid }) => (
  <div className={cx(CARD, 'px-4 py-3.5')} data-testid={testid}>
    <p className={LABEL}>{label}</p>
    <p className={cx('text-2xl font-bold tabular-nums tracking-tight', tone || 'text-[var(--ink)]')}>{value}</p>
    {hint && <p className="mt-0.5 text-xs text-[var(--muted)]">{hint}</p>}
  </div>
);
